import type { EmployeeCompetency } from '@/types/manager-assessment/Competency';
import type { CompetencyDto, CompetencyGroup } from '@/types/review/ReviewDiscussion';

/** The group a competency without one is filed under. */
const FALLBACK_GROUP_NAME = 'Other';

interface RatedCompetency {
    competencyId: string;
    assessedProficiencyLevelId?: string | null;
}

interface SkillCategoryEntry {
    name: string;
    skills: { id: string; name: string; isDeactivated?: boolean; isDeleted?: boolean }[];
}

interface BuildCompetencyGroupsParams {
    /** The competencies the assessment rated, with the level each one got. */
    ratedCompetencies: RatedCompetency[];
    /** The competencies assigned to the employee today. */
    employeeCompetencies: Pick<
        EmployeeCompetency,
        | 'id'
        | 'name'
        | 'description'
        | 'groupName'
        | 'lastAssessedAt'
        | 'isDeactivated'
        | 'isDeleted'
    >[];
    /** The employee's skillset, where a rated competency no longer assigned is still found. */
    skillCategories: SkillCategoryEntry[];
    /** Proficiency levels from the highest to the lowest. */
    orderedLevels: { id?: string | null }[];
}

/**
 * The rated competencies of an assessment, grouped for the handout: first those still assigned to
 * the employee, in their order, then those rated but no longer assigned, found in the skillset.
 * A competency scores the number of levels below and including its own, 0 when it has no level.
 */
export const buildCompetencyGroups = ({
    ratedCompetencies,
    employeeCompetencies,
    skillCategories,
    orderedLevels,
}: BuildCompetencyGroupsParams): CompetencyGroup[] => {
    const ratedLevelIds = new Map(
        ratedCompetencies.map((rated) => [
            rated.competencyId,
            rated.assessedProficiencyLevelId ?? null,
        ]),
    );
    const levelRanks = new Map(
        orderedLevels.flatMap((level, rank) => (level.id ? [[level.id, rank] as const] : [])),
    );
    const groups = new Map<string, CompetencyGroup>();

    const addToGroup = (
        groupName: string | null,
        competency: Omit<CompetencyDto, 'assessedScore' | 'proficiencyLevelId'>,
    ) => {
        const name = groupName?.trim() || FALLBACK_GROUP_NAME;
        const group = groups.get(name) ?? {
            id: name.toLowerCase().replace(/\s+/g, '-'),
            name,
            competencies: [],
        };
        groups.set(name, group);

        const levelId = ratedLevelIds.get(competency.id) ?? null;
        const rank = levelId ? levelRanks.get(levelId) : undefined;
        group.competencies.push({
            ...competency,
            assessedScore: rank === undefined ? 0 : orderedLevels.length - rank,
            proficiencyLevelId: levelId ?? undefined,
        });
    };

    employeeCompetencies
        .filter((competency) => ratedLevelIds.has(competency.id))
        .forEach((competency) =>
            addToGroup(competency.groupName, {
                id: competency.id,
                name: competency.name,
                description: competency.description ?? '',
                lastAssessedAt: competency.lastAssessedAt,
                isDeactivated: competency.isDeactivated,
                isDeleted: competency.isDeleted,
            }),
        );

    const assignedIds = new Set(employeeCompetencies.map((competency) => competency.id));
    const skillsById = new Map(
        skillCategories.flatMap((category) =>
            category.skills.map(
                (skill) => [skill.id, { skill, groupName: category.name }] as const,
            ),
        ),
    );

    ratedCompetencies.forEach(({ competencyId }) => {
        const entry = assignedIds.has(competencyId) ? undefined : skillsById.get(competencyId);
        if (!entry) {
            return;
        }

        addToGroup(entry.groupName, {
            id: entry.skill.id,
            name: entry.skill.name,
            description: '',
            lastAssessedAt: null,
            isDeactivated: entry.skill.isDeactivated ?? false,
            isDeleted: entry.skill.isDeleted ?? false,
        });
    });

    return [...groups.values()];
};
